import React, { useCallback, useEffect, useState } from 'react';
import apiService from '../services/ApiService';
import PageHeader, { RefreshButton } from '../components/shell/PageHeader';
import { useToast } from '../contexts/ToastContext';

interface AuditRow {
    id: number;
    userId?: number;
    userEmail?: string;
    method: string;
    path: string;
    ipAddress?: string;
    userAgent?: string;
    status: number;
    durationMs: number;
    createdAt: string;
}

const METHOD_BADGE: Record<string, string> = {
    GET: 'bg-secondary',
    POST: 'bg-primary',
    PUT: 'bg-info text-dark',
    PATCH: 'bg-info text-dark',
    DELETE: 'bg-danger',
};

const AuditLogPage: React.FC = () => {
    const { showToast } = useToast();
    const [rows, setRows] = useState<AuditRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [sinceHours, setSinceHours] = useState(24);
    const [onlyErrors, setOnlyErrors] = useState(false);
    const [pathContains, setPathContains] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await apiService.adminGetAuditLog<AuditRow>({
                sinceHours,
                minStatus: onlyErrors ? 400 : undefined,
                pathContains: pathContains.trim() || undefined,
                limit: 500,
            });
            setRows(data);
        } catch {
            showToast('Errore', 'Impossibile caricare l\'audit log', 'danger');
        } finally {
            setLoading(false);
        }
    }, [sinceHours, onlyErrors, pathContains, showToast]);
    useEffect(() => { void load(); }, [load]);

    return (
        <>
            <PageHeader
                title="Audit log"
                description="Ogni operazione amministrativa, con esito e durata. Il corpo delle richieste è salvato solo come hash."
                actions={<RefreshButton onClick={load} loading={loading} />}
            />

            <div className="card shadow-sm mb-3">
                <div className="card-body">
                    <div className="row g-2 align-items-end">
                        <div className="col-6 col-md-2">
                            <label className="form-label small">Periodo</label>
                            <select className="form-select form-select-sm"
                                value={sinceHours} onChange={e => setSinceHours(Number(e.target.value))}>
                                <option value={1}>1 ora</option>
                                <option value={6}>6 ore</option>
                                <option value={24}>24 ore</option>
                                <option value={168}>7 giorni</option>
                                <option value={720}>30 giorni</option>
                            </select>
                        </div>
                        <div className="col-12 col-md">
                            <label className="form-label small">Filtro path</label>
                            <input type="text" className="form-control form-control-sm"
                                placeholder="es. tools, audit, users…"
                                value={pathContains} onChange={e => setPathContains(e.target.value)} />
                        </div>
                        <div className="col-6 col-md-auto">
                            <div className="form-check pb-1">
                                <input type="checkbox" className="form-check-input" id="onlyErrors"
                                    checked={onlyErrors} onChange={e => setOnlyErrors(e.target.checked)} />
                                <label className="form-check-label small" htmlFor="onlyErrors">
                                    Solo errori (4xx/5xx)
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="card shadow-sm">
                <div className="table-responsive">
                    <table className="table table-hover align-middle table-sm mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>Quando</th>
                                <th>Utente</th>
                                <th>Method</th>
                                <th>Path</th>
                                <th>Status</th>
                                <th className="text-end">Durata</th>
                                <th>IP</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={7} className="text-center text-body-secondary p-4">
                                    <span className="spinner-border spinner-border-sm me-2" />
                                    Caricamento…
                                </td></tr>
                            )}
                            {!loading && rows.length === 0 && (
                                <tr><td colSpan={7} className="text-center text-body-secondary p-4">
                                    Nessuna riga.
                                </td></tr>
                            )}
                            {!loading && rows.map(r => (
                                <tr key={r.id}>
                                    <td className="small text-body-secondary" style={{ whiteSpace: 'nowrap' }}>
                                        {new Date(r.createdAt).toLocaleString('it-IT')}
                                    </td>
                                    <td className="small">{r.userEmail ?? <em>—</em>}</td>
                                    <td>
                                        <span className={`badge ${METHOD_BADGE[r.method] ?? 'bg-secondary'}`}>
                                            {r.method}
                                        </span>
                                    </td>
                                    <td><code className="small">{r.path}</code></td>
                                    <td>
                                        <span className={`badge ${r.status >= 500 ? 'bg-danger' : r.status >= 400 ? 'bg-warning text-dark' : 'bg-success'}`}>
                                            {r.status}
                                        </span>
                                    </td>
                                    <td className="text-end small">{r.durationMs} ms</td>
                                    <td className="small text-body-secondary">
                                        <code>{r.ipAddress || '—'}</code>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
};

export default AuditLogPage;
